import React, { useState, useEffect } from "react";
import { Modal, Box, Typography, Button, CircularProgress } from "@mui/material";
import { continueSession, endSession } from "../../utils/sessionTimeout";

/**
 * SessionTimeoutModal
 * ----------------------------------------------------------------------------
 * Displays a warning to the user when their session is about to expire due to
 * inactivity. The user can either "Continue Session" (resets the inactivity
 * timer) or "Logout" (immediately ends the session).
 *
 * This modal is rendered by App.js when the session timeout monitor fires its
 * onWarning callback.
 */

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 420,
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  p: 4,
  outline: "none",
  textAlign: "center",
};

const SessionTimeoutModal = ({ open, onClose }) => {
  const [secondsLeft, setSecondsLeft] = useState(60);

  // Count down from 60 seconds while the modal is open
  useEffect(() => {
    if (!open) return;

    setSecondsLeft(60);
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open]);

  const handleContinue = () => {
    continueSession();
    onClose();
  };

  const handleLogout = () => {
    endSession();
  };

  return (
    <Modal
      open={open}
      onClose={handleContinue}
      aria-labelledby="session-timeout-title"
      aria-describedby="session-timeout-description"
    >
      <Box sx={modalStyle}>
        <Box sx={{ position: "relative", display: "inline-block", mb: 2 }}>
          <CircularProgress
            variant="determinate"
            value={(secondsLeft / 60) * 100}
            size={72}
            thickness={4}
            sx={{ color: "#ed4022" }}
          />
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              {secondsLeft}
            </Typography>
          </Box>
        </Box>

        <Typography
          id="session-timeout-title"
          variant="h6"
          component="h2"
          sx={{ fontWeight: 600, mb: 1 }}
        >
          Session Expiring
        </Typography>

        <Typography
          id="session-timeout-description"
          variant="body2"
          sx={{ color: "text.secondary", mb: 3, lineHeight: 1.6 }}
        >
          Your session is about to expire due to inactivity. You will be logged
          out in <strong>{secondsLeft} seconds</strong> unless you continue.
        </Typography>

        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <Button
            variant="contained"
            onClick={handleContinue}
            sx={{
              bgcolor: "#ed4022",
              "&:hover": { bgcolor: "#d63a1e" },
              textTransform: "none",
              px: 3,
            }}
          >
            Continue Session
          </Button>
          <Button
            variant="outlined"
            onClick={handleLogout}
            sx={{
              color: "#6b7280",
              borderColor: "#d1d5db",
              "&:hover": { borderColor: "#9ca3af", bgcolor: "#f9fafb" },
              textTransform: "none",
              px: 3,
            }}
          >
            Logout
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SessionTimeoutModal;